import { useRef } from "react";
import { Camera, User } from "lucide-react";
import type { CandidateProfile } from "@applicant-reviewer/api";
import { useUploadMedia } from "../../../../hooks/mutations/useMediaMutations";
import { useUpdateProfile } from "../../../../hooks/mutations/useProfileMutations";

interface ProfileHeaderProps {
  profile: CandidateProfile;
}

export default function ProfileHeader({ profile }: ProfileHeaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMedia = useUploadMedia();
  const updateProfile = useUpdateProfile();

  const displayName =
    profile.firstName || profile.lastName
      ? `${profile.firstName ?? ""} ${profile.lastName ?? ""}`.trim()
      : "Your Name";

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    uploadMedia.mutate(
      {
        request: {
          fileName: file.name,
          contentType: file.type,
          applicationId: "profile",
        },
        file,
      },
      {
        onSuccess: (result) => {
          updateProfile.mutate({ photoMediaId: result.fileKey });
        },
      },
    );
  };

  const isPending = uploadMedia.isPending || updateProfile.isPending;

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col items-center gap-4">
      {/* Avatar */}
      <div className="relative">
        <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden border-2 border-gray-200">
          {profile.photoMediaId ? (
            <img
              src={`/media/${profile.photoMediaId}`}
              alt="Profile photo"
              className="w-full h-full object-cover"
            />
          ) : (
            <User className="w-12 h-12 text-gray-400" />
          )}
        </div>
        {isPending && (
          <div className="absolute inset-0 rounded-full bg-white/70 flex items-center justify-center">
            <div className="w-6 h-6 border-2 border-[#0156FF] border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {/* Name */}
      <p className="text-xl font-extrabold text-gray-900" style={{ fontFamily: "Nunito, sans-serif" }}>
        {displayName}
      </p>
      <p className="text-sm text-gray-500 -mt-3">{profile.email}</p>

      {/* Change Photo Button */}
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={isPending}
        className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-[#0156FF] border border-[#0156FF] rounded-full hover:bg-blue-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <Camera className="w-4 h-4" />
        Change Photo
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handlePhotoChange}
      />

      {uploadMedia.isError && (
        <p className="text-xs text-red-600">Photo upload failed. Please try again.</p>
      )}
    </div>
  );
}
